/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ApplicationWindow.tsx - Ventana Flotante de Aplicaciones
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @description Ventana flotante arrastrable y redimensionable para aplicaciones WASP
 * @version 1.0.0
 * @architecture WASP Frontend
 * 
 * FUNCIONALIDADES:
 * - ✅ Drag & drop
 * - ✅ Resize
 * - ✅ Minimize/Maximize
 * - ✅ Fullscreen (navegador)
 * - ✅ F11-style fullscreen
 * - ✅ Soporte dark/light theme
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useRef, useEffect } from 'react';
import { Card, Button, ButtonGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faMinus, 
  faExpand, 
  faCompress,
  faMaximize,
  faGripVertical
} from '@fortawesome/free-solid-svg-icons';
import { WindowPosition, WindowSize } from '../hooks/useWindowManager';

interface ApplicationWindowProps {
  id: string;
  appId: string;
  appName: string;
  appIcon: any;
  position: WindowPosition;
  size: WindowSize;
  zIndex: number;
  onClose: () => void;
  onMinimize: () => void;
  onPositionChange: (position: WindowPosition) => void;
  onSizeChange: (size: WindowSize) => void;
  onBringToFront: () => void;
  children: React.ReactNode;
}

export const ApplicationWindow: React.FC<ApplicationWindowProps> = ({
  id,
  appId,
  appName,
  appIcon,
  position,
  size,
  zIndex,
  onClose,
  onMinimize,
  onPositionChange,
  onSizeChange,
  onBringToFront,
  children
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [previousState, setPreviousState] = useState<{ position: WindowPosition; size: WindowSize } | null>(null);
  
  const windowRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.window-controls')) return;
    
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
    onBringToFront();
  };

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height
    });
    onBringToFront();
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && !isFullscreen && !isMaximized) {
        onPositionChange({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y
        });
      }

      if (isResizing && !isFullscreen && !isMaximized) {
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;
        
        onSizeChange({
          width: Math.max(400, resizeStart.width + deltaX),
          height: Math.max(300, resizeStart.height + deltaY)
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragOffset, resizeStart, position, size, isFullscreen, isMaximized]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      windowRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const toggleMaximize = () => {
    if (isMaximized) {
      if (previousState) {
        onPositionChange(previousState.position);
        onSizeChange(previousState.size);
        setPreviousState(null);
      }
      setIsMaximized(false);
    } else {
      setPreviousState({ position, size });
      onPositionChange({ x: 0, y: 0 });
      onSizeChange({ width: window.innerWidth, height: window.innerHeight });
      setIsMaximized(true);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const windowStyle: React.CSSProperties = {
    position: 'fixed',
    left: isMaximized ? 0 : position.x,
    top: isMaximized ? 0 : position.y,
    width: isMaximized ? '100vw' : size.width,
    height: isMaximized ? '100vh' : size.height,
    zIndex: 9999 + zIndex,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: isMaximized 
      ? 'none' 
      : '0 8px 32px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
    borderRadius: isMaximized ? '0' : '10px',
    overflow: 'hidden',
    backgroundColor: 'var(--bs-body-bg)',
    border: isMaximized ? 'none' : '0.5px solid var(--bs-border-color)',
    transition: isMaximized ? 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
  };

  return (
    <div ref={windowRef} style={windowStyle}>
      <div
        onMouseDown={handleMouseDown}
        style={{
          padding: '10px 16px',
          backgroundColor: 'var(--bs-body-bg)',
          borderBottom: '0.5px solid var(--bs-border-color)',
          cursor: isDragging ? 'grabbing' : 'grab',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          userSelect: 'none',
          minHeight: '48px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 1px 3px rgba(102, 126, 234, 0.2)'
            }}
          >
            <FontAwesomeIcon icon={appIcon} style={{ color: '#fff', fontSize: '0.9rem' }} />
          </div>
          <span style={{ fontWeight: 600, color: 'var(--bs-body-color)', fontSize: '0.9rem' }}>
            {appName}
          </span>
        </div>

        <ButtonGroup size="sm" className="window-controls" style={{ gap: '2px' }}>
          <Button
            variant="link"
            onClick={toggleMaximize}
            title={isMaximized ? "Restaurar" : "Maximizar"}
            style={{ 
              padding: '4px 8px',
              color: 'var(--bs-secondary)',
              textDecoration: 'none',
              borderRadius: '4px',
              transition: 'all 0.15s',
              minWidth: '28px'
            }}
            className="window-control-btn"
          >
            <FontAwesomeIcon icon={isMaximized ? faCompress : faExpand} style={{ fontSize: '0.8rem' }} />
          </Button>
          <Button
            variant="link"
            onClick={toggleFullscreen}
            title="Pantalla completa (F11)"
            style={{ 
              padding: '4px 8px',
              color: 'var(--bs-secondary)',
              textDecoration: 'none',
              borderRadius: '4px',
              transition: 'all 0.15s',
              minWidth: '28px'
            }}
            className="window-control-btn"
          >
            <FontAwesomeIcon icon={faMaximize} style={{ fontSize: '0.8rem' }} />
          </Button>
          <Button
            variant="link"
            onClick={onMinimize}
            title="Minimizar"
            style={{ 
              padding: '4px 8px',
              color: '#ffc107',
              textDecoration: 'none',
              borderRadius: '4px',
              transition: 'all 0.15s',
              minWidth: '28px'
            }}
            className="window-control-btn"
          >
            <FontAwesomeIcon icon={faMinus} style={{ fontSize: '0.8rem' }} />
          </Button>
          <Button
            variant="link"
            onClick={onClose}
            title="Cerrar"
            style={{ 
              padding: '4px 8px',
              color: '#dc3545',
              textDecoration: 'none',
              borderRadius: '4px',
              transition: 'all 0.15s',
              minWidth: '28px'
            }}
            className="window-control-btn"
          >
            <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.8rem' }} />
          </Button>
        </ButtonGroup>
      </div>

      <div
        style={{
          flex: 1,
          overflow: 'auto',
          backgroundColor: 'var(--bs-body-bg)',
          position: 'relative'
        }}
      >
        {children}
      </div>

      {!isFullscreen && !isMaximized && (
        <div
          onMouseDown={handleResizeMouseDown}
          style={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            width: '24px',
            height: '24px',
            cursor: 'nwse-resize',
            background: 'linear-gradient(135deg, transparent 50%, var(--bs-secondary) 50%)',
            opacity: 0.4,
            borderTopLeftRadius: '6px',
            transition: 'opacity 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
          onMouseLeave={(e) => e.currentTarget.style.opacity = '0.4'}
        />
      )}

      <style>{`
        .window-control-btn:hover {
          background-color: rgba(0, 0, 0, 0.05) !important;
        }
        
        [data-bs-theme="dark"] .window-control-btn:hover {
          background-color: rgba(255, 255, 255, 0.1) !important;
        }
      `}</style>
    </div>
  );
};

export default ApplicationWindow;